import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import MessageStream from "./MessageStream";
import { useSimpleChat } from "@/contexts/SimpleChatContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { WHATSAPP } from "@/lib/brand";
import { trackContactClick } from "@/lib/analytics";
import { blurClear, fadeUp, staggerContainer } from "@/lib/animations";
import { cn } from "@/lib/utils";

const MAX_INPUT_LEN = 500;

/**
 * Hero-centered LLM chat surface. Two visual modes:
 *
 *   - **Empty** — eyebrow + big heading + subtitle + large input + 4
 *     starter-prompt chips. The page hero, basically.
 *   - **Active** — once the user sends their first message, the heading
 *     collapses to a thin header strip and the conversation panel takes the
 *     centre, with the input pinned at the bottom of the surface.
 *
 * Lead capture is fully agent-driven (`submit_lead` tool); when the streaming
 * `done` event reports `completed: true` we show a thin inline banner above
 * the conversation but keep the chat alive so the user can keep asking.
 */
const HeroChat = () => {
    const { t } = useTranslation();
    const { lang, dir } = useLanguage();
    const chat = useSimpleChat();

    const [input, setInput] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const isAr = lang === "ar";
    const heroFontClass = isAr ? "font-v2-hero-ar" : "font-v2-hero-en";

    const hasMessages = chat.messages.length > 0;
    const whatsappUrl = `${WHATSAPP.sy.url}?text=${encodeURIComponent(
        t("common.whatsapp_initial_project", {
            defaultValue: "أهلاً، حابب أحكي عن مشروع",
        }),
    )}`;

    // i18next's `returnObjects` gives us back the raw array. Defensive default
    // in case the locale file hasn't been updated yet for this language.
    const suggestions = (() => {
        const raw = t("hero_chat.suggestions", { returnObjects: true });
        return Array.isArray(raw) ? (raw as string[]) : [];
    })();

    // Auto-focus the input once the session is ready, so users can start
    // typing without an extra click.
    useEffect(() => {
        if (chat.sessionId && !hasMessages) {
            inputRef.current?.focus();
        }
    }, [chat.sessionId, hasMessages]);

    // Keep the textarea height matching its content, capped so we never
    // dominate the viewport on phones.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [input]);

    const submit = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || chat.isLoading) return;
        void chat.onTextSubmit(trimmed);
        setInput("");
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        submit(input);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Enter sends, Shift+Enter inserts newline — same convention as
        // ChatGPT/Claude landing pages.
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit(input);
        }
    };

    const showCharCounter = input.length >= MAX_INPUT_LEN * 0.8;

    return (
        <section
            id="hero-chat"
            dir={dir}
            className="relative min-h-[100svh] flex flex-col justify-between pt-24 sm:pt-28 pb-8 sm:pb-12 overflow-hidden"
            aria-labelledby="hero-chat-heading"
        >
            {/* Top brutalist rule with kicker labels */}
            <div className="container mx-auto px-5 sm:px-6 mt-4">
                <div className="flex items-center justify-between gap-4 pb-3 border-b border-white/12">
                    <span className="font-mono-brutal text-[10px] sm:text-xs text-white/55">
                        {t("hero_chat.kicker_left", {
                            defaultValue: t("hero.kicker_left", {
                                defaultValue: "TRBD · ASSISTANT",
                            }),
                        })}
                    </span>
                    <span aria-hidden className="hidden sm:block flex-1 h-px bg-white/10" />
                    <span className="font-mono-brutal text-[10px] sm:text-xs text-white/55 flex items-center gap-2">
                        <span className="relative inline-flex w-1.5 h-1.5">
                            <span className="absolute inset-0 bg-success animate-ping opacity-50" />
                            <span className="relative inline-block w-1.5 h-1.5 bg-success" />
                        </span>
                        <span className="text-success/85">
                            {t("hero_chat.kicker_right", {
                                defaultValue: "ONLINE",
                            })}
                        </span>
                    </span>
                </div>
            </div>

            {/* Main content */}
            <div className="container mx-auto px-5 sm:px-6 relative z-10 w-full flex-1 flex items-center">
                <motion.div
                    key={hasMessages ? "active" : "empty"}
                    className="w-full max-w-4xl mx-auto"
                    variants={staggerContainer(0.1, 0.05)}
                    initial="hidden"
                    animate="show"
                >
                    {/* Heading + subtitle — only in empty state */}
                    {!hasMessages && (
                        <>
                            <motion.div
                                variants={fadeUp}
                                className="flex items-center justify-center gap-3 mb-8 sm:mb-10"
                            >
                                <span aria-hidden className="brutal-glyph" style={{ height: "0.7em" }} />
                                <span className="font-mono-brutal text-[10px] sm:text-[11px] text-white/60">
                                    {t("hero_chat.eyebrow", {
                                        defaultValue: "ASK · TRBD",
                                    })}
                                </span>
                                <span aria-hidden className="h-px w-12 bg-white/10" />
                            </motion.div>

                            <h1
                                id="hero-chat-heading"
                                className={cn(
                                    heroFontClass,
                                    "text-white text-center mb-6 sm:mb-8 leading-[1.05] rtl:leading-[1.25]",
                                )}
                                style={{
                                    fontSize: isAr
                                        ? "clamp(36px, 8.5vw, 120px)"
                                        : "clamp(34px, 8vw, 128px)",
                                }}
                            >
                                <motion.span variants={blurClear} className="block">
                                    {t("hero_chat.title", {
                                        defaultValue: "تجاوز النموذج.",
                                    })}
                                </motion.span>
                                <motion.span variants={blurClear} className="block">
                                    <span
                                        style={{
                                            backgroundImage: "var(--gradient-accent-v2)",
                                            WebkitBackgroundClip: "text",
                                            backgroundClip: "text",
                                            color: "transparent",
                                        }}
                                    >
                                        {t("hero_chat.title_highlight", {
                                            defaultValue: "كلّمنا مباشرة.",
                                        })}
                                    </span>
                                </motion.span>
                            </h1>

                            <motion.p
                                variants={fadeUp}
                                className="text-base sm:text-lg md:text-xl text-white/70 leading-relaxed text-center max-w-2xl mx-auto mb-8 sm:mb-10"
                            >
                                {t("hero_chat.subtitle", {
                                    defaultValue:
                                        "احكِ مع المساعد الذكي عن فكرتك — يفهم، يجاوب، ويوصلها لفريقنا.",
                                })}
                            </motion.p>
                        </>
                    )}

                    {/* Active conversation panel */}
                    {hasMessages && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="mb-4 sm:mb-5 bg-white/[0.015] border border-white/15 relative overflow-hidden flex flex-col h-[55svh] sm:h-[60svh]"
                        >
                            <span
                                aria-hidden
                                className="absolute top-0 inset-x-0 h-px pointer-events-none"
                                style={{
                                    background: "var(--gradient-accent-v2)",
                                    opacity: 0.7,
                                }}
                            />
                            {chat.completed && (
                                <div className="px-4 py-2.5 border-b border-success/30 bg-success/[0.06] flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                                    <span className="text-xs sm:text-sm text-white/85 leading-snug">
                                        {t("hero_chat.lead_sent_banner", {
                                            defaultValue:
                                                "تم استلام طلبك ✓ — رح نتواصل معك خلال 24 ساعة.",
                                        })}
                                    </span>
                                </div>
                            )}
                            <MessageStream
                                messages={chat.messages}
                                onRetry={chat.onRetry}
                            />
                        </motion.div>
                    )}

                    {/* Input area */}
                    <motion.form
                        variants={fadeUp}
                        onSubmit={handleSubmit}
                        className={cn(
                            "relative bg-white/[0.015] border transition-colors",
                            isFocused ? "border-white/40" : "border-white/15",
                        )}
                    >
                        <span
                            aria-hidden
                            className="absolute top-0 inset-x-0 h-px pointer-events-none"
                            style={{
                                background: "var(--gradient-accent-v2)",
                                opacity: 0.7,
                            }}
                        />
                        <div className="flex items-end gap-2 p-3 sm:p-4">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                onKeyDown={handleKeyDown}
                                placeholder={t("hero_chat.placeholder", {
                                    defaultValue: "اسألني عن مشروعك...",
                                })}
                                disabled={chat.isLoading || !chat.sessionId}
                                rows={1}
                                maxLength={MAX_INPUT_LEN}
                                dir="auto"
                                className={cn(
                                    "flex-1 bg-transparent resize-none outline-none",
                                    "text-base sm:text-lg text-white placeholder:text-white/35",
                                    "min-h-[28px] max-h-[160px] py-1.5 px-1",
                                    "disabled:opacity-50",
                                )}
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || chat.isLoading || !chat.sessionId}
                                aria-label={t("hero_chat.send_aria", {
                                    defaultValue: "إرسال",
                                })}
                                className={cn(
                                    "shrink-0 relative w-11 h-11 flex items-center justify-center text-white transition-opacity",
                                    "disabled:opacity-40 disabled:cursor-not-allowed",
                                    "enabled:hover:opacity-90 enabled:active:opacity-80",
                                )}
                                style={{ background: "var(--gradient-accent-v2)" }}
                            >
                                <Send className="w-4 h-4 rtl:-scale-x-100" />
                            </button>
                        </div>
                        {showCharCounter && (
                            <div className="absolute end-3 -top-2.5">
                                <span
                                    className={cn(
                                        "font-mono-brutal text-[9px] px-1.5 py-0.5 border bg-background",
                                        input.length >= MAX_INPUT_LEN
                                            ? "border-destructive/50 text-destructive"
                                            : "border-white/15 text-white/55",
                                    )}
                                >
                                    {input.length}/{MAX_INPUT_LEN}
                                </span>
                            </div>
                        )}
                    </motion.form>

                    {/* Suggestion chips — empty state only */}
                    {!hasMessages && suggestions.length > 0 && (
                        <motion.div
                            variants={fadeUp}
                            className="flex flex-wrap justify-center gap-2 mt-5 sm:mt-6"
                        >
                            {suggestions.map((prompt) => (
                                <button
                                    key={prompt}
                                    type="button"
                                    onClick={() => submit(prompt)}
                                    disabled={chat.isLoading || !chat.sessionId}
                                    className={cn(
                                        "px-3.5 py-2 text-xs sm:text-sm font-bold border transition-colors",
                                        "border-white/15 text-white/75 bg-white/[0.02]",
                                        "hover:border-white/30 hover:text-white hover:bg-white/[0.05]",
                                        "disabled:opacity-50 disabled:cursor-not-allowed",
                                    )}
                                >
                                    {prompt}
                                </button>
                            ))}
                        </motion.div>
                    )}

                    {/* WhatsApp fallback link */}
                    <motion.div
                        variants={fadeUp}
                        className="flex items-center justify-center gap-2 mt-5 sm:mt-6"
                    >
                        <span aria-hidden className="h-px w-8 bg-white/10" />
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cursor="hover"
                            onClick={() =>
                                trackContactClick("whatsapp_sy", "hero_chat")
                            }
                            className="font-mono-brutal text-[10px] sm:text-[11px] text-white/50 hover:text-white/85 transition-colors flex items-center gap-1.5"
                        >
                            <MessageCircle className="w-3 h-3" />
                            {t("hero_chat.whatsapp_fallback", {
                                defaultValue: "أو راسلنا على واتساب",
                            })}
                        </a>
                        <span aria-hidden className="h-px w-8 bg-white/10" />
                    </motion.div>
                </motion.div>
            </div>

            {/* Bottom brutalist rule */}
            <div className="container mx-auto px-5 sm:px-6 mt-12 sm:mt-16">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="flex items-center gap-4 pt-3 border-t border-white/12"
                >
                    <span className="font-mono-brutal text-[10px] text-white/40 whitespace-nowrap">
                        {t("hero.scroll", { defaultValue: "SCROLL ↓" })}
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-white/10" />
                    <span className="font-mono-brutal text-[10px] sm:text-xs text-white/55 truncate">
                        {t("hero.trust_strip", {
                            defaultValue: "45+ مشروع · 102+ عميل · 7+ سنوات",
                        })}
                    </span>
                </motion.div>
            </div>
        </section>
    );
};

export default HeroChat;
